import React, { useState, useEffect, useCallback } from 'react';
import { pacienteService } from '../services';
import Modal from './Modal';
import { 
  Search, 
  Users,
  CheckCircle
} from 'lucide-react';

const SeletorPacientes = ({ isOpen, onClose, onSelect, pacienteSelecionado }) => {
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const carregarPacientes = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search
      };
      
      const response = await pacienteService.getAll(params);
      setPacientes(response.pacientes || []);
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      console.error('Erro ao carregar pacientes:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, search]);

  useEffect(() => {
    if (isOpen) {
      carregarPacientes();
    }
  }, [isOpen, search, currentPage, carregarPacientes]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleSelectPaciente = (paciente) => {
    onSelect(paciente);
    onClose();
  };

  const formatarCpf = (cpf) => {
    if (!cpf) return '-';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatarCartaoSus = (cartao) => {
    if (!cartao) return '-';
    return cartao.replace(/(\d{3})(\d{4})(\d{4})(\d{4})/, '$1 $2 $3 $4');
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Selecionar Paciente" 
      size="large"
    >
      {/* Busca */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ position: 'relative' }}>
          <Search size={20} style={{ 
            position: 'absolute', 
            left: '10px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            color: '#667'
          }} />
          <input
            type="text"
            className="form-control"
            placeholder="Buscar por nome, CPF ou cartão SUS..."
            value={search}
            onChange={handleSearch}
            style={{ paddingLeft: '40px' }}
            autoFocus
          />
        </div>
      </div>

      {/* Lista de Pacientes */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner"></div>
        </div>
      ) : pacientes.length > 0 ? (
        <>
          <div style={{ 
            maxHeight: '400px', 
            overflowY: 'auto',
            border: '1px solid #ddd',
            borderRadius: '8px'
          }}>
            {pacientes.map((paciente) => (
              <div
                key={paciente.id}
                onClick={() => handleSelectPaciente(paciente)}
                style={{
                  padding: '15px',
                  borderBottom: '1px solid #eee',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: pacienteSelecionado?.id === paciente.id ? '#e3f2fd' : 'white',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (pacienteSelecionado?.id !== paciente.id) {
                    e.target.style.backgroundColor = '#f5f5f5';
                  }
                }}
                onMouseLeave={(e) => {
                  if (pacienteSelecionado?.id !== paciente.id) {
                    e.target.style.backgroundColor = 'white';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    backgroundColor: '#007bff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '18px'
                  }}>
                    {paciente.nome.charAt(0).toUpperCase()}
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: 'bold', 
                      fontSize: '16px',
                      marginBottom: '5px',
                      color: '#333'
                    }}>
                      {paciente.nome}
                    </div>
                    
                    <div style={{ 
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '10px',
                      fontSize: '14px',
                      color: '#667'
                    }}>
                      <div>
                        <strong>CPF:</strong> {formatarCpf(paciente.cpf)}
                      </div>
                      {paciente.cartaoSus && (
                        <div>
                          <strong>Cartão SUS:</strong> {formatarCartaoSus(paciente.cartaoSus)}
                        </div>
                      )}
                      {paciente.rg && (
                        <div>
                          <strong>RG:</strong> {paciente.rg}
                        </div>
                      )}
                      {paciente.endereco && (
                        <div style={{ gridColumn: 'span 2' }}>
                          <strong>Endereço:</strong> {paciente.endereco}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {pacienteSelecionado?.id === paciente.id ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      color: '#28a745',
                      fontWeight: 'bold'
                    }}>
                      <CheckCircle size={20} />
                      Selecionado
                    </div>
                  ) : (
                    <div style={{
                      padding: '8px 16px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      Selecionar
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              gap: '15px',
              marginTop: '20px',
              padding: '15px',
              borderTop: '1px solid #eee'
            }}>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="btn btn-secondary"
                style={{ padding: '8px 16px' }}
              >
                Anterior
              </button>
              
              <span style={{ color: '#667' }}>
                Página {currentPage} de {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="btn btn-secondary"
                style={{ padding: '8px 16px' }}
              >
                Próxima
              </button>
            </div>
          )}
        </>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          color: '#667'
        }}>
          <Users size={48} style={{ opacity: 0.5, marginBottom: '15px' }} />
          <h3>Nenhum paciente encontrado</h3>
          <p>
            {search 
              ? 'Tente ajustar o termo de busca' 
              : 'Não há pacientes cadastrados'
            }
          </p>
        </div>
      )}

      {/* Rodapé */}
      <div style={{ 
        borderTop: '1px solid #eee',
        padding: '15px 0 0 0',
        marginTop: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ fontSize: '14px', color: '#667' }}>
          {pacientes.length > 0 && (
            <>Mostrando {pacientes.length} paciente{pacientes.length !== 1 ? 's' : ''}</>
          )}
        </div>
        
        <button 
          onClick={onClose}
          className="btn btn-secondary"
        >
          Cancelar
        </button>
      </div>
    </Modal>
  );
};

export default SeletorPacientes;